import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import BaseScreen from 'components/base-screen';
import BottomSheet from 'components/bottom-sheet';
import Button, { ButtonProps } from 'components/button';
import BottomSheetResult from 'components/bottom-sheet-result';
import ApartmentListItem from 'components/apartment-list-item';

import useBoundStore from 'store';
import { BuyApartmentFlowPaths, SellApartmentFlowPaths } from 'routes';
import useApartmentSaleInfo, { APIApartmentSellInfoRes } from 'api/hooks/use-apartment-sale-info';

import styles from './styles.module.scss';
import useFacilityConstants from '../../api/hooks/use-facility-constants';
import { ruble } from '../../components/price-calculator';
import Switchbox from '../../components/switchbox';
import clsx from 'clsx';
import useApartment from '../../api/hooks/use-apartment';
import { editSaleAd } from '../../api';
import NumberInput from '../../components/number-input';

export type InputApartmentPriceNavigateProps = {
  apartment: APIApartmentSellInfoRes & { id: number; price: number };
};

const SellApartmentNotificationMode: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const reportConstant = useFacilityConstants();
  const [maternalCapital, setMaternalCapital] = useState(false);
  const [encumbrance, setEncumbrance] = useState(false);
  const [understatement, setUnderstatement] = useState(false);

  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);
  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);
  const sellApartmentNumber = useBoundStore((s) => s.sellApartmentNumber);

  const navigationProps = useLocation().state as InputApartmentPriceNavigateProps | undefined;

  // @ts-ignore
  const { apartment } = useApartment(id);

  const saleInfo = useApartmentSaleInfo(
    selectedSellBuilding.id,
    navigationProps ? '' : sellApartmentNumber,
  );

  const apartmentSellInfo = apartment
    ? {
        id: apartment?.id,
        price: apartment?.price,
        apartmentComplexTitle: apartment?.housingEstate ?? '',
        areaInSqM: apartment?.area,
        ceilingHeight: apartment?.ceilingHeight,
        floor: apartment?.floor.floor,
        floorCount: apartment?.floor.total,
        roomCount: apartment?.rooms,
        planImage: apartment?.images?.[0]?.src,
      }
    : saleInfo.apartmentSellInfo;

  useEffect(() => {}, [apartment]);

  interface IPriceStat {
    days: number;
    price: number;
    percent: number;
  }

  const [selectedPrice, setSelectedPrice] = useState<{
    stat: IPriceStat;
    value: number;
  } | null>(null);

  const [priceWithoutMarketPrise, setPriceWithoutMarketPrise] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [minorOwners, setMinorOwners] = useState(false);

  const buttons: ButtonProps[] = [
    {
      label: 'Продолжить',
      loading: isSubmitting,
      onClick: async () => {
        setIsSubmitting(true);
        const editAdRes = await editSaleAd(
          apartmentSellInfo?.id,
          apartmentSellInfo?.price ?? null,
          +priceWithoutMarketPrise,
          maternalCapital,
          encumbrance,
          minorOwners,
        );
        setIsSubmitting(false);

        if (editAdRes.result && apartment) {
          const state: InputApartmentPriceNavigateProps = {
            // @ts-ignore
            apartment: {
              id: apartment.id,
              price: apartment.price,
              apartmentComplexTitle: apartment.housingEstate ?? '',
              areaInSqM: apartment.area,
              ceilingHeight: apartment.ceilingHeight,
              floor: apartment.floor.floor,
              floorCount: apartment.floor.total,
              roomCount: apartment.rooms,
              planImage: apartment.images[0]?.src,
            },
          };
          navigate(SellApartmentFlowPaths.documentsNotificationMode, { state });
        }
      },
    },
    {
      label: 'Отмена',
      variant: 'transparent',
      onClick: () => {
        navigate(BuyApartmentFlowPaths.main);
      },
    },
  ];

  const handleDismissBottomSheet = () => {
    setIsBottomSheetOpen(false);
    navigate(BuyApartmentFlowPaths.main);
  };

  const getSwitchboxProps = (label: string) => ({
    label,
    className: clsx(styles.onlyAvailable, styles.hidden),
  });

  return (
    <BaseScreen
      padContent
      title='Продать объект'
      buttons={buttons}
      onBackButtonClick={() => navigate(-1)}
    >
      <>
        <h1 className={styles.title}>Хотите продать быстрее добавьте информацию </h1>
      </>
      <div className={styles.container}>
        <ApartmentListItem
          isFavorite={false}
          price={apartmentSellInfo?.price}
          squarePrice={
            apartmentSellInfo?.areaInSqM && priceWithoutMarketPrise
              ? // @ts-ignore
                ~~(priceWithoutMarketPrise / apartmentSellInfo.areaInSqM)
              : undefined
          }
          image={apartmentSellInfo?.planImage ?? ''}
          rooms={apartmentSellInfo?.roomCount ?? 0}
          area={apartmentSellInfo?.areaInSqM ?? 0}
          housingEstate={selectedSellComplex.title}
          floor={
            apartmentSellInfo
              ? { floor: apartmentSellInfo.floor, total: apartmentSellInfo.floorCount }
              : { floor: 0, total: 0 }
          }
        />

        <Switchbox
          {...getSwitchboxProps('Занижение')}
          labelPosition='left'
          checked={understatement}
          onChange={setUnderstatement}
          className={styles.containerSwitchBox}
        />
        {understatement ? (
          <>
            <div>
              <span className='text-medium'>Укажите сумму занижения</span>

              <NumberInput
                sign={ruble}
                value={priceWithoutMarketPrise}
                onReset={() => {
                  setPriceWithoutMarketPrise('');
                }}
                onChange={setPriceWithoutMarketPrise}
                className={styles.priceInput}
              />
            </div>
          </>
        ) : null}
        <div className={styles.line} />
        <Switchbox
          {...getSwitchboxProps('Несовершеннолетние собственники')}
          labelPosition='left'
          checked={!!minorOwners}
          onChange={() => {
            setMinorOwners(!minorOwners);
          }}
          className={styles.containerSwitchBox}
        />
        <div className={styles.line} />
        <Switchbox
          {...getSwitchboxProps('Обременение')}
          labelPosition='left'
          checked={encumbrance}
          onChange={setEncumbrance}
          className={styles.containerSwitchBox}
        />
        <div className={styles.line} />

        <Switchbox
          {...getSwitchboxProps('Мат.капитал')}
          labelPosition='left'
          checked={maternalCapital}
          onChange={setMaternalCapital}
          className={styles.containerSwitchBox}
        />
        <div className={styles.line} />
      </div>

      <BottomSheet open={isBottomSheetOpen} onDismiss={handleDismissBottomSheet}>
        <BottomSheetResult type='info' header='Заявка принята'>
          Ваша заявка отправлена на модерацию
        </BottomSheetResult>
        <Button label='Отлично' onClick={handleDismissBottomSheet} />
      </BottomSheet>
    </BaseScreen>
  );
};

export default SellApartmentNotificationMode;
